import {
  Box,
  Button,
  DismissIcon,
  Hide,
  Image,
  Typography,
  useMobileMediaQuery,
} from '@iheartjane/reefer';
import { trackError } from '@iheartjane/tracking';
import { useCallback, useRef, useState } from 'react';
import type { ControllerRenderProps, FieldValues } from 'react-hook-form';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

import { useCheckout } from '@jane/shared-ecomm/providers';
/* eslint-disable-next-line no-restricted-imports */
import type { Identification } from '@jane/shared/models';
import { ImagePreview, convertIfPDF } from '@jane/shared/util';

import styles from './FormImageUpload.module.css';
import { ALLOWED_FILE_TYPES } from './allowedFileTypes';
import { useUppy } from './useUppy';

export const FormImageUpload = ({
  defaultValue,
  fileName,
  label,
  name,
}: {
  defaultValue?: string;
  fileName: keyof Identification;
  label: string;
  name: `customerInformation.${string}`;
}) => {
  const [error, setError] = useState('');
  const { setValue } = useFormContext();
  const value = useWatch({ name, defaultValue });

  const isMobile = useMobileMediaQuery({ size: 'lg', width: 'max' });
  const {
    customer: { authenticated },
    dispatches: { updateIdentification },
  } = useCheckout();

  const onFileChange = useCallback(
    (url: string) => {
      setValue(name, url, { shouldValidate: true });
    },
    [name, setValue]
  );

  const {
    changeFile,
    error: uppyError,
    removeFile,
    status,
    thumbnail,
  } = useUppy({
    image: defaultValue,
    name,
    onChange: onFileChange,
  });

  const hiddenInput = useRef<HTMLInputElement>(null);

  const handleAddFile = async (
    field: ControllerRenderProps<FieldValues, `customerInformation.${string}`>,
    file?: File
  ) => {
    // For unauthenticated user, we upload the file to aws and store the aws url.
    if (!authenticated) {
      await changeFile(file);
      return;
    }

    // For authenticated users, we parse the file and send the base64 encode file to the documents controller
    if (file) {
      ImagePreview.parse(file).then((result) => {
        convertIfPDF(result.url).then(
          (result) => {
            field.onChange(result);
            updateIdentification({ document: fileName, file: result });
          },
          (err) => {
            trackError(err, file.slice(0, 100));
            setError(
              'Unable to convert PDF file. Please choose a different image.'
            );
            field.onChange(result.url);
          }
        );
      });
    }
  };

  const handleRemoveFile = (
    field: ControllerRenderProps<FieldValues, `customerInformation.${string}`>
  ) => {
    // The html input maintains the previous file even though we clear it from react hook form,
    // which prevents us from uploading the same file again, as it assumes it already has it.
    if (hiddenInput?.current) {
      hiddenInput.current.value = '';
    }

    if (!authenticated) {
      removeFile();
    } else {
      field.onChange('');
      updateIdentification({ document: fileName, file: null });
    }
  };

  const imgSrc = thumbnail || value;

  return (
    <Controller
      defaultValue={defaultValue}
      rules={{
        required: true,
      }}
      name={name}
      render={({ field }) => {
        return (
          <>
            <Hide isHidden hideFromScreenReader>
              <input
                type="file"
                ref={hiddenInput}
                onChange={(e) => handleAddFile(field, e.target.files?.[0])}
                accept={ALLOWED_FILE_TYPES.join(',')}
              />
            </Hide>
            {!imgSrc && (
              <Button
                variant="secondary"
                label={`Upload ${label}`}
                loading={status === 'inProgress'}
                onClick={() => hiddenInput.current?.click()}
              />
            )}
            {imgSrc && (
              <Box
                position="relative"
                border="grays-light"
                borderRadius="lg"
                width={isMobile ? '145px' : '185px'}
              >
                <Image
                  borderRadius="rounded"
                  height={isMobile ? '100px' : '125px'}
                  objectFit="cover"
                  src={imgSrc}
                  altText={`${label} preview`}
                />

                <Box top={-8} right={-8} position="absolute">
                  <Button.Icon
                    className={styles['borderedButton']}
                    variant="minimal-inverse"
                    icon={
                      <DismissIcon
                        size="sm"
                        aria-label={`Remove ${label} image`}
                      />
                    }
                    onClick={() => handleRemoveFile(field)}
                  />
                </Box>
              </Box>
            )}
            {(error || uppyError) && (
              <Typography color="error" mt={16} role="alert">
                {error ? error : uppyError}
              </Typography>
            )}
          </>
        );
      }}
    />
  );
};
